<script>
  import Icon from "@iconify/svelte";
  import { onMount } from "svelte";
  import { t } from "../lib/i18n.svelte.js";
  import { getUser, logout } from "../lib/stores/auth.svelte.js";
  import { api } from "../lib/api.svelte.js";
  import { getTheme, toggleTheme, getAccentColor, setAccentColor, COLOR_SWATCHES } from "../lib/stores/theme.svelte.js";
  import LanguageSwitcher from "./LanguageSwitcher.svelte";
  import { push } from "svelte-spa-router";

  let mobileOpen = $state(false);
  let paletteOpen = $state(false);
  let accent = $derived(getAccentColor());
  let user = $derived(getUser());
  let isDark = $derived(getTheme() === "dark");

  const navLinks = [
    { href: "/invites", label: () => t("nav.invites"), icon: "mdi:email-outline" },
    { href: "/sites", label: () => t("nav.sites"), icon: "mdi:web" },
    { href: "/viewers", label: () => t("nav.viewers"), icon: "mdi:account-group" },
    { href: "/access", label: () => t("nav.access"), icon: "mdi:shield-key-outline" },
    { href: "/storage", label: () => t("nav.storage"), icon: "streamline:cloud-share" },
    { href: "/settings", label: () => t("nav.settings"), icon: "mdi:cog" },
    { href: "/runtime", label: () => t("nav.runtime"), icon: "mdi:console" },
  ];

  function isActive(href) {
    const hash = window.location.hash.slice(1) || "/";
    return hash === href || hash.startsWith(href + "/");
  }

  async function handleLogout() {
    await logout();
    push("/login");
  }

  function go(href) {
    push(href);
    mobileOpen = false;
  }

  function handleWindowClick() {
    if (paletteOpen) paletteOpen = false;
  }

  onMount(() => {
    window.addEventListener("click", handleWindowClick);
    return () => window.removeEventListener("click", handleWindowClick);
  });
</script>

<div class="w-full pt-3 px-3 sm:pt-4 sm:px-6">
  <header class="sticky top-3 z-50 ui-topbar w-full">
    <div class="w-full px-3 py-2 sm:px-4 sm:py-3">
      <div class="flex items-center justify-between gap-2">
        <!-- Logo + name (always visible) -->
        <div class="flex items-center gap-2 min-w-0">
          <button type="button" class="ui-logo flex-shrink-0" aria-label="Go to dashboard" onclick={() => go("/dashboard")}>
            <Icon icon="streamline:cloud-share" class="w-[24px] h-[24px]" />
          </button>
          <button type="button" class="flex flex-col leading-tight min-w-0 text-left hover:opacity-80 transition-opacity" onclick={() => go("/dashboard")}>
            <div class="text-sm sm:text-lg font-semibold tracking-wide text-white truncate">snapshot</div>
            <div class="text-[10px] sm:text-xs text-zinc-400 truncate hidden sm:block">{t("app.tagline")}</div>
          </button>
        </div>

        <!-- Desktop: nav + controls -->
        <div class="hidden md:flex items-center justify-end gap-3">
          <nav class="topnav topnav--nowrap">
            {#each navLinks as link}
              <button
                type="button"
                class="topnav__tab"
                class:is-active={isActive(link.href)}
                onclick={() => go(link.href)}
                title={link.label()}
                aria-label={link.label()}
              >
                <Icon icon={link.icon} class="w-4 h-4 shrink-0" />
                <span class="whitespace-nowrap">{link.label()}</span>
              </button>
            {/each}
          </nav>

          <a href="#/sites/new" class="ui-btn-accent" title={t("sites.new_site")} aria-label={t("sites.new_site")}>
            <Icon icon="mdi:plus" class="w-5 h-5" />
          </a>
          <a href="#/viewers/new" class="ui-btn-primary" title={t("viewers.new")} aria-label={t("viewers.new")}>
            <Icon icon="lets-icons:user-add-light" class="w-5 h-5" />
          </a>

          <div class="w-px h-6 bg-white/10 mx-1"></div>

          <!-- Accent colour picker -->
          <div class="relative">
            <button
              type="button"
              class="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] transition-colors"
              onclick={(e) => { e.stopPropagation(); paletteOpen = !paletteOpen; }}
              title={t("nav.accent_colour")}
            >
              <span class="w-4 h-4 rounded-full" style="background:{accent}"></span>
            </button>
            {#if paletteOpen}
              <!-- svelte-ignore a11y_no_static_element_interactions -->
              <div
                class="absolute right-0 top-12 z-[201] w-[220px] p-3 rounded-xl border ui-dropdown shadow-2xl"
                onclick={(e) => e.stopPropagation()}
              >
                <p class="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">{t("nav.accent_colour")}</p>
                <div class="grid grid-cols-8 gap-1.5">
                  {#each COLOR_SWATCHES as swatch}
                    <button
                      type="button"
                      class="w-5 h-5 rounded-full border transition-all hover:scale-125 {accent === swatch.value ? 'border-white ring-1 ring-white/50 scale-125' : 'border-white/10'}"
                      style="background:{swatch.value}"
                      title={swatch.name}
                      onclick={() => { setAccentColor(swatch.value); paletteOpen = false; }}
                    ></button>
                  {/each}
                </div>
              </div>
            {/if}
          </div>

          <button
            type="button"
            class="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] text-zinc-200 transition-colors"
            onclick={toggleTheme}
            title={isDark ? t("nav.light_mode") : t("nav.dark_mode")}
          >
            <Icon icon={isDark ? "mdi:weather-sunny" : "mdi:weather-night"} class="w-4 h-4" />
          </button>

          <LanguageSwitcher />

          {#if user}
            <button
              type="button"
              class="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-white/10 bg-white/[0.035] hover:bg-rose-500/20 hover:text-rose-200 text-zinc-200 transition-colors"
              onclick={handleLogout}
              title={t("nav.logout")}
            >
              <Icon icon="mdi:logout" class="w-4 h-4" />
            </button>
          {/if}
        </div>

        <!-- Mobile: hamburger -->
        <button
          type="button"
          class="md:hidden inline-flex items-center justify-center w-9 h-9 rounded border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] text-zinc-200"
          aria-label={mobileOpen ? t("nav.closeMenu") : t("nav.openMenu")}
          onclick={() => (mobileOpen = !mobileOpen)}
        >
          <Icon icon={mobileOpen ? "mdi:close" : "mdi:menu"} class="w-5 h-5" />
        </button>
      </div>
    </div>
  </header>
</div>

<!-- Mobile: full-screen overlay -->
{#if mobileOpen}
  <div class="md:hidden fixed inset-0 z-[100] bg-black mobile-overlay flex flex-col" role="dialog" aria-label="Menu">
    <!-- Top bar -->
    <div class="flex items-center justify-between px-4 py-3">
      <div class="flex items-center gap-2">
        <div class="ui-logo !w-8 !h-8">
          <Icon icon="streamline:cloud-share" class="w-[24px] h-[24px]" />
        </div>
        <span class="text-base font-semibold text-white">snapshot</span>
      </div>
      <button type="button" class="p-2 text-zinc-400 hover:text-white" onclick={() => (mobileOpen = false)}>
        <Icon icon="mdi:close" class="w-6 h-6" />
      </button>
    </div>

    <!-- Nav grid -->
    <div class="flex-1 overflow-y-auto px-6 py-4">
      <nav class="space-y-1">
        {#each navLinks as link}
          <button
            type="button"
            class="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-base transition-colors {isActive(link.href) ? 'text-white bg-white/[0.08]' : 'text-zinc-400 hover:text-white hover:bg-white/[0.04]'}"
            onclick={() => go(link.href)}
          >
            <Icon icon={link.icon} class="w-5 h-5 shrink-0" />
            {link.label()}
          </button>
        {/each}
      </nav>

      <!-- Action buttons (icon only, consistent with desktop) -->
      <div class="flex items-center gap-3 mt-6 px-4">
        <a href="#/sites/new" class="ui-btn-accent" title={t("sites.new_site")} onclick={() => (mobileOpen = false)}>
          <Icon icon="mdi:plus" class="w-5 h-5" />
        </a>
        <a href="#/viewers/new" class="ui-btn-primary" title={t("viewers.new")} onclick={() => (mobileOpen = false)}>
          <Icon icon="lets-icons:user-add-light" class="w-5 h-5" />
        </a>
      </div>
    </div>

    <!-- Bottom controls -->
    <div class="shrink-0 px-6 pb-6 space-y-4">
      <!-- Accent palette (above controls so it expands upward) -->
      {#if paletteOpen}
        <div class="grid grid-cols-6 gap-2.5 p-3 rounded-lg border border-white/10 bg-white/[0.04]">
          {#each COLOR_SWATCHES as swatch}
            <button
              type="button"
              class="w-7 h-7 rounded-full border-2 transition-all {accent === swatch.value ? 'border-white scale-110' : 'border-transparent'}"
              style="background:{swatch.value}"
              onclick={() => { setAccentColor(swatch.value); paletteOpen = false; }}
            ></button>
          {/each}
        </div>
      {/if}

      <!-- Controls row -->
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="w-8 h-8 rounded-full border-2 border-white/20"
          style="background:{accent}"
          onclick={() => (paletteOpen = !paletteOpen)}
        ></button>
        <button
          type="button"
          class="w-8 h-8 rounded-lg border border-white/10 bg-white/[0.04] text-zinc-300 flex items-center justify-center"
          onclick={toggleTheme}
        >
          <Icon icon={isDark ? "mdi:weather-sunny" : "mdi:weather-night"} class="w-4 h-4" />
        </button>
        <div class="relative">
          <LanguageSwitcher />
        </div>
        <div class="flex-1"></div>
        {#if user}
          <button
            type="button"
            class="w-8 h-8 rounded-lg border border-white/10 bg-white/[0.04] text-zinc-400 hover:text-rose-400 flex items-center justify-center transition-colors"
            onclick={() => { handleLogout(); mobileOpen = false; }}
            title={t("nav.logout")}
          >
            <Icon icon="mdi:logout" class="w-4 h-4" />
          </button>
        {/if}
      </div>
    </div>
  </div>
{/if}
